import { AnchorButton } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import omit from 'lodash.omit';
import * as React from 'react';

import { Tooltip } from 'bones/components/atoms/Tooltip';
import { ExpressionCascade } from 'bones/components/molecules/QueryBuilder/ExpressionCascade';
import { ExpressionCascadeButton } from 'bones/components/molecules/QueryBuilder/ExpressionCascadeButton';
import {
  useGetClassValueItems,
  useGetComparisonPropertyItems,
  useGetComparisonSubjectItems,
  useGetPropertyItemsForClass,
} from 'bones/components/molecules/QueryBuilder/hooks/selectorItems';
import { useQueryBuilderContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilder';
import { useQueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import {
  ANY_CLASS_IRI,
  DEFAULT_OPERATOR_TYPE_BY_DATATYPE_RANGE,
  DatatypeOperatorType,
  DatatypeOperatorTypeValue,
  InstanceOperatorType,
  ObjectOperatorType,
  OperatorType,
} from 'bones/utils/queryBuilder/constants';
import { createNewFilter } from 'bones/utils/queryBuilder/helpers/create';
import { getValueForNewOperator } from 'bones/utils/queryBuilder/helpers/getValueForNewOperator';
import { PropertySubSelectItem } from 'bones/utils/queryBuilder/helpers/select';
import {
  DraftDatatypeComparisonOperator,
  DraftDatatypeOperator,
  DraftExpression,
  DraftFilter,
  Variable,
} from 'bones/utils/queryBuilder/types';

export interface ExpressionBlockProps {
  subExpressionIdx: number;
  expressionIdx: number;
  removeExpression: () => void;
  updateExpression: (expression: DraftExpression, validate?: boolean) => void;
}

export const ExpressionBlock: React.VFC<ExpressionBlockProps> = ({
  subExpressionIdx,
  expressionIdx,
  removeExpression,
  updateExpression,
}) => {
  const { copy, testIds, andNotDisabled, limitOperators } =
    useQueryBuilderSchemaContext();
  const { topExpression } = useQueryBuilderContext();
  const getPropertyItems = useGetPropertyItemsForClass();
  const getValueItems = useGetClassValueItems();
  const getComaprisonSubjectItems = useGetComparisonSubjectItems();
  const getComaprisonPropertyItems = useGetComparisonPropertyItems();
  const subExpression = topExpression.expressions[subExpressionIdx];
  const expression = subExpression.expressions[expressionIdx];

  const lastFilter = expression.filters[expression.filters.length - 1];

  const handleAddFilter = () => {
    let subject: Variable | null = null;
    if (
      !andNotDisabled &&
      lastFilter.value &&
      typeof lastFilter.value !== 'string'
    ) {
      subject = lastFilter.value;
    } else if (lastFilter.comparisonSubject) {
      subject = lastFilter.comparisonSubject;
    } else {
      subject = lastFilter.subject;
    }

    updateExpression({
      ...expression,
      filters: [...expression.filters, createNewFilter(subject)],
    });
  };

  const updateFilter = (
    filterIdx: number,
    validate: boolean,
    newFilter?: DraftFilter
  ) => {
    const filters = [...expression.filters];
    if (newFilter) {
      filters.splice(filterIdx, 1, newFilter);
    } else {
      filters.splice(filterIdx, 1);
    }

    updateExpression({ ...expression, filters }, validate);
  };

  const handleRemoveFilter = (filterIdx: number) => () => {
    // always validate on removal
    updateFilter(filterIdx, true);
  };

  const handleSelectSubject = (filterIdx: number) => (subject: Variable) => {
    const filter = expression.filters[filterIdx];
    if (filter.subject?.id === subject.id) {
      return;
    }

    let newFilter = createNewFilter(subject);
    let validate = false;

    const isPropertyItemValid = (item: PropertySubSelectItem) =>
      item.iri === filter.operator?.iri &&
      (item.operatorType !== OperatorType.OBJECT ||
        item.direction === filter.operator?.direction);

    // replace existing instance if the property for the next subject is valid
    const currentIri = filter.subject?.iri;
    if (
      subject.iri === ANY_CLASS_IRI ||
      subject.iri === currentIri ||
      (filter.operator &&
        getPropertyItems(subject.iri, {
          subExpressionIdx,
          expressionIdx,
          filterIdx,
        }).some((item) =>
          item.items
            ? item.items.some(isPropertyItemValid)
            : isPropertyItemValid(item as PropertySubSelectItem)
        ))
    ) {
      newFilter = {
        ...filter,
        subject,
        isInvalid: { ...filter.isInvalid, subject: false },
        tooltip: { ...filter.tooltip, subject: '' },
      };
      // validate when replacing an existing subject
      validate = Boolean(currentIri);
    }

    updateFilter(filterIdx, validate, newFilter);
  };

  const handleSelectProperty =
    (filterIdx: number) =>
    ({ iri, direction, operatorType, range }: PropertySubSelectItem) => {
      const filter = expression.filters[filterIdx];
      if (
        filter.operator &&
        filter.operator.iri === iri &&
        (filter.operator.operatorType !== OperatorType.OBJECT ||
          filter.operator.direction === direction)
      ) {
        return;
      }

      const newFilter: DraftFilter = {
        ...filter,
        comparisonSubject: null,
        comparisonOperator: null,
        value: null,
        isInvalid: {
          ...filter.isInvalid,
          property: false,
          operator: false,
          comparisonSubject: false,
          comparisonProperty: false,
          value: false,
        },
        tooltip: {
          ...filter.tooltip,
          property: '',
          comparisonSubject: '',
          comparisonProperty: '',
          value: '',
        },
      };

      if (operatorType === OperatorType.INSTANCE) {
        newFilter.operator = {
          iri,
          operatorType,
          type: InstanceOperatorType.IN,
        };
      } else if (operatorType === OperatorType.DATATYPE) {
        let type: DatatypeOperatorTypeValue | undefined;
        if (limitOperators) {
          if (range) {
            type = DEFAULT_OPERATOR_TYPE_BY_DATATYPE_RANGE[range];
            if (type === DatatypeOperatorType.IS) {
              newFilter.value = 'false';
            }
          }
          if (!type) {
            type = DatatypeOperatorType.EQUALS;
          }
        }
        newFilter.operator = {
          iri,
          operatorType,
          range,
          type,
        };
      } else {
        newFilter.operator = {
          iri,
          direction,
          operatorType,
          type: ObjectOperatorType.CONNECTED,
        };

        const possibleValues = getValueItems(newFilter.operator, {
          subExpressionIdx,
          expressionIdx,
          filterIdx,
        });
        const filteredPossibleValues = possibleValues.filter(
          ({ value }) => value !== ANY_CLASS_IRI
        );

        let value: Variable | null = null;
        if (
          filter.value &&
          typeof filter.value !== 'string' &&
          possibleValues.some((item) =>
            item.items?.some(
              (subItem) =>
                typeof filter.value !== 'string' &&
                subItem.iri === filter.value?.iri
            )
          )
        ) {
          // leave existing value if valid
          value = filter.value;
        } else if (
          filteredPossibleValues.length === 1 &&
          filteredPossibleValues[0].items?.length === 1
        ) {
          // auto-select the only valid value
          const nextVaue = filteredPossibleValues[0].items[0];
          value = { iri: nextVaue.iri, id: nextVaue.value };
        }
        newFilter.value = value;
      }

      // validate when replacing an existing object operator value
      // validate when replacing an existing comparison subject
      const validate =
        Boolean(
          filter.value &&
            typeof filter.value !== 'string' &&
            (!newFilter.value ||
              (typeof newFilter.value !== 'string' &&
                filter.value.iri !== newFilter.value.iri))
        ) || Boolean(filter.comparisonSubject);

      updateFilter(filterIdx, validate, newFilter);
    };

  const handleSelectOperator =
    (filterIdx: number) =>
    (operatorType: DatatypeOperatorTypeValue, comparison: boolean) => {
      const filter = expression.filters[filterIdx];
      if (
        filter?.operator?.operatorType !== OperatorType.DATATYPE ||
        (filter.operator?.type === operatorType &&
          Boolean(filter.operator?.comparison) === comparison)
      ) {
        return;
      }

      const newFilter: DraftFilter = {
        ...filter,
        isInvalid: {
          ...filter.isInvalid,
          operator: false,
          comparisonSubject: false,
          comparisonProperty: false,
          value: false,
        },
        tooltip: {
          ...filter.tooltip,
          comparisonSubject: '',
          comparisonProperty: '',
          value: '',
        },
      };

      if (comparison) {
        newFilter.value = null;

        const operator: DraftDatatypeComparisonOperator = {
          ...filter.operator,
          comparison: true,
          type: operatorType,
        };
        newFilter.operator = operator;

        const possibleSubjects = getComaprisonSubjectItems(operator, {
          subExpressionIdx,
          expressionIdx,
          filterIdx,
        });
        const filteredPossibleSubjects = possibleSubjects.filter(
          ({ value }) => value !== ANY_CLASS_IRI
        );

        let comparisonSubject: Variable | null = null;
        if (
          filter.comparisonSubject &&
          possibleSubjects.some((item) =>
            item.items?.some(
              (subItem) => subItem.iri === filter.comparisonSubject?.iri
            )
          )
        ) {
          // leave existing subject if valid
          comparisonSubject = filter.comparisonSubject;
        } else if (
          filteredPossibleSubjects.length === 1 &&
          filteredPossibleSubjects[0].items?.length === 1
        ) {
          // replace with valid subject if there is only one
          const nextVaue = filteredPossibleSubjects[0].items[0];
          comparisonSubject = { iri: nextVaue.iri, id: nextVaue.value };
        }
        newFilter.comparisonSubject = comparisonSubject;

        let comparisonOperator: DraftDatatypeOperator | null = null;
        if (comparisonSubject) {
          const possibleProperties = getComaprisonPropertyItems(
            comparisonSubject.iri,
            operator
          );

          const isPropertyItemValid = (item: PropertySubSelectItem) =>
            item.iri === filter.comparisonOperator?.iri;

          if (
            filter.comparisonOperator &&
            possibleProperties.some((item) =>
              item.items
                ? item.items.some(isPropertyItemValid)
                : isPropertyItemValid(item as PropertySubSelectItem)
            )
          ) {
            // leave existing operator if valid
            comparisonOperator = filter.comparisonOperator;
          } else if (
            possibleProperties.length === 1 &&
            (!possibleProperties[0].items ||
              possibleProperties[0].items?.length === 1)
          ) {
            // replace with valid operator if there is only one
            const foundItem = possibleProperties[0].items
              ? possibleProperties[0].items[0]
              : (possibleProperties[0] as PropertySubSelectItem);
            comparisonOperator = {
              iri: foundItem.iri,
              operatorType: OperatorType.DATATYPE,
              range: foundItem.range,
              type: DatatypeOperatorType.EQUALS,
            };
          }
        }
        newFilter.comparisonOperator = comparisonOperator;
      } else {
        newFilter.comparisonSubject = null;
        newFilter.comparisonOperator = null;

        const value = getValueForNewOperator({
          operatorType,
          prevOperatorType: filter.operator.type,
          range: filter.operator.range || null,
          value: filter.value as string | null,
        });
        newFilter.value = value;

        const operator: DraftDatatypeOperator = {
          ...omit(filter.operator, 'comparison'),
          type: operatorType,
        };
        newFilter.operator = operator;
      }

      // validate when switching from a comparsion to an attribute value
      // validate when replacing an existing comparison subject
      const validate =
        Boolean(filter.operator.comparison && !comparison) ||
        Boolean(
          filter.comparisonSubject &&
            (!newFilter.comparisonSubject ||
              filter.comparisonSubject.iri !== newFilter.comparisonSubject.iri)
        );

      updateFilter(filterIdx, validate, newFilter);
    };

  const handleSelectComparisonSubject =
    (filterIdx: number) => (comparisonSubject: Variable) => {
      const filter = expression.filters[filterIdx];
      if (filter.comparisonSubject?.id === comparisonSubject.id) {
        return;
      }

      const newFilter: DraftFilter = {
        ...filter,
        comparisonSubject,
        isInvalid: {
          ...filter.isInvalid,
          comparisonSubject: false,
          comparisonProperty: false,
        },
        tooltip: {
          ...filter.tooltip,
          comparisonSubject: '',
          comparisonProperty: '',
        },
      };

      const possibleProperties = getComaprisonPropertyItems(
        comparisonSubject.iri,
        filter.operator
      );

      const isPropertyItemValid = (item: PropertySubSelectItem) =>
        item.iri === filter.comparisonOperator?.iri;

      let comparisonOperator: DraftDatatypeOperator | null = null;
      if (
        filter.comparisonOperator &&
        possibleProperties.some((item) =>
          item.items
            ? item.items.some(isPropertyItemValid)
            : isPropertyItemValid(item as PropertySubSelectItem)
        )
      ) {
        // leave existing operator if valid
        comparisonOperator = filter.comparisonOperator;
      } else if (
        possibleProperties.length === 1 &&
        (!possibleProperties[0].items ||
          possibleProperties[0].items?.length === 1)
      ) {
        // replace with valid operator if there is only one
        const foundItem = possibleProperties[0].items
          ? possibleProperties[0].items[0]
          : (possibleProperties[0] as PropertySubSelectItem);
        comparisonOperator = {
          iri: foundItem.iri,
          operatorType: OperatorType.DATATYPE,
          range: foundItem.range,
          type: DatatypeOperatorType.EQUALS,
        };
      }
      newFilter.comparisonOperator = comparisonOperator;

      // validate when replacing an existing comparison subject
      const validate = Boolean(filter.comparisonSubject?.iri);

      updateFilter(filterIdx, validate, newFilter);
    };

  const handleSelectComparisonProperty =
    (filterIdx: number) =>
    ({ iri, operatorType, range }: PropertySubSelectItem) => {
      // istanbul ignore if
      if (operatorType !== OperatorType.DATATYPE) {
        return;
      }
      const filter = expression.filters[filterIdx];
      if (filter.comparisonOperator && filter.comparisonOperator.iri === iri) {
        return;
      }

      const newFilter: DraftFilter = {
        ...filter,
        comparisonOperator: {
          iri,
          operatorType,
          range,
          type: DatatypeOperatorType.EQUALS,
        },
        isInvalid: {
          ...filter.isInvalid,
          comparisonProperty: false,
        },
        tooltip: {
          ...filter.tooltip,
          comparisonProperty: '',
        },
      };

      updateFilter(filterIdx, false, newFilter);
    };

  const handleSetValue = (filterIdx: number) => (value: string | Variable) => {
    const filter = expression.filters[filterIdx];

    // istanbul ignore if
    if (typeof value === 'string' && filter.value === value) {
      return;
    }

    const newFilter: DraftFilter = {
      ...filter,
      value,
      isInvalid: {
        ...filter.isInvalid,
        value: false,
      },
      tooltip: {
        ...filter.tooltip,
        value: '',
      },
    };

    let validate = false;
    // istanbul ignore else
    if (typeof value !== 'string' && typeof filter.value !== 'string') {
      if (filter.value && filter.value.id === value.id) {
        return;
      }

      // always validate when setting or replacing an instance
      validate = true;
    } else if (typeof value === 'string') {
      // reset value to null for empty instances to hide "add cascade" button on clear
      newFilter.value =
        filter.operator?.operatorType === OperatorType.INSTANCE
          ? value || null
          : value;
    }

    updateFilter(filterIdx, validate, newFilter);
  };

  return (
    <div
      className="sd-query-builder-expression"
      data-testid={testIds.expressionContainer}
    >
      <div className="sd-query-builder-filters">
        {expression.filters.map((filter, idx) => (
          <ExpressionCascade
            key={filter.id}
            expressionIdx={expressionIdx}
            filterIdx={idx}
            onRemoveFilter={handleRemoveFilter(idx)}
            onSelectComparisonProperty={handleSelectComparisonProperty(idx)}
            onSelectComparisonSubject={handleSelectComparisonSubject(idx)}
            onSelectOperator={handleSelectOperator(idx)}
            onSelectProperty={handleSelectProperty(idx)}
            onSelectSubject={handleSelectSubject(idx)}
            onSetValue={handleSetValue(idx)}
            subExpressionIdx={subExpressionIdx}
          />
        ))}
        {lastFilter.subject &&
        lastFilter.operator &&
        (lastFilter.value !== null || lastFilter.comparisonOperator) ? (
          <div className="sd-query-builder-expression-cascade">
            <ExpressionCascadeButton
              hoverIcon={IconNames.ADD}
              onClick={handleAddFilter}
              testId={testIds.addExpressionCascadeButton}
            />
          </div>
        ) : null}
      </div>
      <div>
        <Tooltip content={copy.body.expression.remove}>
          <AnchorButton
            data-testid={testIds.removeExpressionButton}
            icon={IconNames.CROSS}
            minimal
            onClick={removeExpression}
          />
        </Tooltip>
      </div>
    </div>
  );
};
